import styled from 'styled-components';

import { Card } from '@components/card';
import { DesignAvatar } from '@components/design-avatar';
import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconCheckmarkSmall } from '@components/icons/checkmark-small';
import { IconCloseSmall } from '@components/icons/close-small';
import { IconHourglass } from '@components/icons/hourglass';
import { IconInfoSmall } from '@components/icons/info-small';
import { IconIntegration } from '@components/icons/integration';
import { IconSend } from '@components/icons/send';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { JsonValue } from '@components/json-editor';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { NoResultCard } from '@components/no-result-card';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingVariables } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { createJourneyActionFieldPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { AIModel, TravellerJourneyActionDetailed, VariableValue } from 'api/types';
import { Property } from 'ui/authenticated/main/pages/ai-agents/agent-details-sidebar';
import { AttributeIcon } from 'ui/authenticated/settings/pages/attributes/common';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import {
  ActionResultCard,
  EditableActionContent,
  FormSidebarFooter,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';

type ActionType = 'ask_ai_agent';

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<ActionType>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }

  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }
  const action = travellerActionQuery.data;
  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={action.values?.name || t('title')}
        icon={() => <DesignAvatar src={action.values?.picture} />}
      >
        <TravellerActionActions action={action} />
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<ActionType>;
};

const FieldMappedTag = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 1,
})({
  backgroundColor: theme.color.blue10,
  borderColor: theme.color.elementBlue100,
  borderWidth: pxToRem(1),
  borderStyle: 'solid',
  color: theme.color.elementBlue100,
  borderRadius: pxToRem(16),
  padding: `${pxToRem(0)} ${pxToRem(8)}`,
  height: pxToRem(24),
  display: 'inline-flex',
});

const TextWithTagsContainer = styled(Text)({
  display: 'inline-flex',
  alignItems: 'center',
  flexWrap: 'wrap',
});

/*
We probably want to refactor this to use a more generic approach to rendering text with tags.
I'll keep this as is for now, but once we use this in other places, we should move it to core
and possibly rewrite it.
*/
const TextWithTags = ({ data }: { data: VariableValue | undefined }) => {
  // Function to replace variables with tags
  const renderTextWithTags = (value: string, variables: VariableValue['variables']) => {
    const parts = value.split(/(\{\{[^}]+\}\})/g);
    return parts.map((part, index) => {
      const match = part.match(/\{\{([^}]+)\}\}/);
      if (match) {
        const variableKey = match[1].trim();
        const variable = variables?.[variableKey];
        if (variable) {
          return (
            <FieldMappedTag key={index}>
              <IconTextFormatLink />
              <Text font="semibold12" color="elementBlue100" text={variable.label} />
            </FieldMappedTag>
          );
        }
      }
      return part;
    });
  };
  if (!data) {
    return null;
  }

  return (
    <TextWithTagsContainer>
      {renderTextWithTags(data.value, data.variables ?? {})}
    </TextWithTagsContainer>
  );
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  const promptLabel = <TextWithTags data={action.values?.prompt} />;
  const isComplete = action.status === 'completed';
  const hasStructuredOutput = action.data?.hasStructuredOutput;

  const outputCards = hasStructuredOutput ? (
    <>
      {action.data.output?.map((output: Property, index: number) => (
        <Card key={index} spaceX={3} spaceY={1} interactive>
          <Stack orientation="horizontal" justify="space-between">
            <Stack orientation="horizontal" space={2} align="center">
              <AttributeIcon type={output.type} />
              <Text key={index} text={output.label} />
              <IconInfoSmall color="purple50" tooltip={output.instructions} />
            </Stack>
          </Stack>
        </Card>
      ))}
    </>
  ) : (
    <Card spaceX={3} spaceY={1} interactive>
      <Stack orientation="horizontal" justify="space-between">
        <Stack orientation="horizontal" space={2} align="center">
          <AttributeIcon type={'paragraph'} />
          <Text text={t('text')} />
          <IconInfoSmall color="purple50" tooltip={t('textInstructions')} />
        </Stack>
      </Stack>
    </Card>
  );

  return (
    <Stack space={6}>
      <HideContainer title={t('actionStatus')}>
        <ActionResultCard
          action={action}
          title={t('title')}
          icon={<IconIntegration />}
          errorTitle={actionErrorTitle[action.action]}
        />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <LabelTextGroup
            label={t('prompt')}
            text={isComplete ? action.data.prompt : promptLabel}
          />
          <div>
            <Text font="semibold14" text={t('output')} />
            <Stack space={1}>{outputCards}</Stack>
          </div>
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('output')}>
        <Output action={action} outputSettings={action.data.output} />
      </HideContainer>
      <Sidebar.Separator />
      <TravellerActionFooter
        action={action}
        startIcon={<IconSend />}
        startText={t('run')}
        retryText={t('retry')}
      />
    </Stack>
  );
};

const Output = ({ action, outputSettings }: ContentProps & { outputSettings: Array<Property> }) => {
  const { t } = useScopedTranslate(sidebarLocale);
  if (action.status !== 'completed') {
    return <NoResultCard icon={IconHourglass} text={t('actionNotRun')} />;
  }
  const response = action.data.response as Record<string, string>;
  if (action.data.hasStructuredOutput) {
    return (
      <Stack space={2}>
        {outputSettings.map((property) => (
          <div key={property.name}>
            <Text font="semibold14" text={property.label} />
            {property.type === 'boolean' ? (
              response[property.name] ? (
                <IconCheckmarkSmall color="green200" />
              ) : (
                <IconCloseSmall color="red200" />
              )
            ) : (
              <Text font="regular14" text={response[property.name]} />
            )}
          </div>
        ))}
      </Stack>
    );
  }
  return <Text font="regular14" text={response['__text']} />;
};

type Data = {
  id: string;
  description: string;
  prompt: VariableValue;
  additionalFiles: FieldMappingVariables[];
  systemInstructions: string;
  model: AIModel;
  jsonSchema?: JsonValue;
  output: Property[];
};

const Form = createForm<Data>();

const FormContent = ({ action }: ContentProps) => {
  const { stopEditing } = useEditingContext();
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateFormStore();
  const output = (formStore.useFieldValue('output') as Property[]) ?? action.values?.output;
  const isDirty = formStore.useIsDirty();
  const fieldMappingId = getFieldMappingId(action.journeyId);

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = async (formData: Data) => {
    const newValues = {
      ...action.values,
      ...formData,
    };
    await editMutation.mutateAsync({
      description: action.description,
      isAutomatic: true,
      action: 'ask_ai_agent',
      values: { ...newValues, jsonSchema: newValues.jsonSchema ?? undefined },
    });
  };

  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    excludedTypes: ['file', 'file_multi'],
  });

  const fileGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['file', 'file_multi'],
  });

  const uploadMutation = useUploadFile(
    createJourneyActionFieldPath(action.journeyId, action.shortId),
    true
  );

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField isTraveller defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('input')}>
          <Stack space={4}>
            <Form.FieldMappingTextEditor
              id="prompt"
              label={t('prompt')}
              variableGroups={variableGroups}
              defaultValue={action.values?.prompt}
              plaintext
              validators={['required']}
              fieldMappingId={fieldMappingId}
            />
            <Form.FieldMappingFileListField
              id="additionalFiles"
              label={t('additionalFiles')}
              variableGroups={fileGroups}
              defaultValue={action.values?.additionalFiles}
              onSelect={uploadMutation.mutateAsync}
              fieldMappingId={fieldMappingId}
            />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('output')}>
          <Stack space={2}>
            {output?.map((output, index) => (
              <Card key={index} spaceX={3} spaceY={1} interactive>
                <Stack orientation="horizontal" justify="space-between">
                  <Stack orientation="horizontal" space={2} align="center">
                    <AttributeIcon type={output.type} />
                    <Text key={index} text={output.name} />
                    <IconInfoSmall color="purple50" tooltip={output.instructions} />
                  </Stack>
                </Stack>
              </Card>
            ))}
          </Stack>
        </HideContainer>
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    askAI: 'Ask AI',
    actionDetails: 'Action details',
    advancedSettings: 'Advanced settings',
    systemInstructions: 'System instructions',
    model: 'Model',
    input: 'Input',
    output: 'Output',
    prompt: 'Prompt',
    additionalFiles: 'Additional files',
    text: 'Text',
    textInstructions: 'Without structured output, AI will return the text in a single field',
    addOutput: 'Add',
    template: 'Prompt templates',
    selectTemplate: 'Select template',
    field: 'Field',
    fieldInfo: 'Field to apply the prompt to',
    infoTitle: 'About Ask AI',
    actionNotRun: 'Action has not been run',
    infoText: 'Ask AI is a tool that helps you analyse and format text data',
    infoPrompt: 'Create the prompt for AI to process selected field',
    infoField: 'Select field/input you want to analyse',
    infoAnalyze: 'AI Analyses the input based on your specifications and guidelines',
    infoOutputToken:
      'AI outputs a token result based on your parameters. This token can be used in various ways, e.g. as a condition for branches',
    infoOutputText:
      'AI outputs a summarised text you can supplement your data with or help you analyse and review content',
    test: 'Test',
    title: 'Ask AI',
    dataNotFound: 'Data not found',
    description: 'Description',
    run: 'Run',
    retry: 'Retry',
    actionStatus: 'Action status',
  },
};
